import { useQuery } from '@tanstack/react-query';
import { Dialog } from '../../components/ui/Dialog';
import { getAgentSession, listAgentSessionEvents } from './api';

export function SessionDetailDialog({
  orgId,
  sessionId,
  onClose,
}: {
  orgId: string;
  sessionId: string;
  onClose: () => void;
}) {
  const session = useQuery({
    queryKey: ['agent-session', orgId, sessionId],
    queryFn: () => getAgentSession(orgId, sessionId),
  });
  const events = useQuery({
    queryKey: ['agent-session-events', orgId, sessionId],
    queryFn: () => listAgentSessionEvents(orgId, sessionId),
  });

  const s = session.data;

  return (
    <Dialog open onClose={onClose} title={s?.name ?? 'Session'}>
      {!s && <p className="text-sm text-ink-tertiary">Loading…</p>}
      {s && (
        <div className="space-y-5 text-sm">
          <section className="grid grid-cols-2 gap-x-4 gap-y-2">
            <Row label="Status" value={s.status} />
            <Row label="Expires" value={new Date(s.expires_at).toLocaleString()} />
            <Row label="Wallet" value={s.wallet_address ?? s.wallet_id} mono span={2} />
          </section>

          <section>
            <Heading>Policies</Heading>
            <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2">
              <Row label="Max per tx (native)" value={s.max_spend_per_tx_native ? weiToEth(s.max_spend_per_tx_native) : 'no limit'} mono />
              <Row label="Max total (native)" value={s.max_spend_total_native ? weiToEth(s.max_spend_total_native) : 'no limit'} mono />
              <Row label="Spent so far" value={weiToEth(s.spent_native)} mono />
              <Row label="Allowed chains" value={s.allowed_chains.length ? s.allowed_chains.join(', ') : 'any'} />
              <Row label="Allowed methods" value={s.allowed_methods.join(', ')} span={2} />
              {s.allowed_recipients && (
                <Row
                  label="Allowed recipients"
                  value={s.allowed_recipients.length ? s.allowed_recipients.join(', ') : '(empty: deny all)'}
                  mono
                  span={2}
                />
              )}
            </div>
          </section>

          <section>
            <Heading>Activity</Heading>
            {events.isLoading && <p className="mt-2 text-xs text-ink-tertiary">Loading…</p>}
            {events.data && events.data.length === 0 && (
              <p className="mt-2 text-xs text-ink-tertiary">No signing activity yet.</p>
            )}
            {events.data && events.data.length > 0 && (
              <ul className="mt-2 space-y-1.5">
                {events.data.map((e) => (
                  <EventRow key={e.ID} kind={e.Kind} detail={e.Detail} at={e.CreatedAt} />
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
    </Dialog>
  );
}

function Heading({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-xs font-medium uppercase tracking-eyebrow text-ink-tertiary">{children}</h3>
  );
}

function Row({ label, value, mono = false, span = 1 }: { label: string; value: string; mono?: boolean; span?: 1 | 2 }) {
  return (
    <div className={span === 2 ? 'col-span-2' : ''}>
      <div className="text-[11px] uppercase tracking-eyebrow text-ink-tertiary">{label}</div>
      <div className={`mt-0.5 ${mono ? 'font-mono break-all text-xs' : 'text-sm'} text-ink-primary`}>
        {value}
      </div>
    </div>
  );
}

function EventRow({ kind, detail, at }: { kind: string; detail: Record<string, unknown>; at: string }) {
  const color =
    kind === 'sign.allowed'
      ? 'border-ok/30 bg-ok/5'
      : kind === 'sign.rejected'
        ? 'border-danger/30 bg-danger/5'
        : 'border-line-soft bg-surface-soft/30';

  const label = labelFor(kind);
  const summary = summarize(kind, detail);

  return (
    <li className={`rounded border px-3 py-2 ${color}`}>
      <div className="flex items-center justify-between text-xs">
        <span className="font-medium">{label}</span>
        <span className="text-ink-tertiary">{new Date(at).toLocaleString()}</span>
      </div>
      {summary && <div className="mt-1 text-xs text-ink-secondary">{summary}</div>}
    </li>
  );
}

function labelFor(kind: string): string {
  switch (kind) {
    case 'session.created':
      return 'Session created';
    case 'session.revoked':
      return 'Session revoked';
    case 'sign.allowed':
      return 'Signature';
    case 'sign.rejected':
      return 'Rejected by policy';
    default:
      return kind;
  }
}

function summarize(kind: string, d: Record<string, unknown>): string | null {
  const method = pickStr(d, 'method');
  const chain = pickStr(d, 'chain');
  const to = pickStr(d, 'to');
  const value = pickStr(d, 'value');
  const reason = pickStr(d, 'reason');

  if (kind === 'sign.allowed' || kind === 'sign.rejected') {
    const parts: string[] = [];
    if (method) parts.push(method);
    if (chain) parts.push(`on ${chain}`);
    if (to) parts.push(`→ ${shortAddr(to)}`);
    if (value && value !== '0') parts.push(`value ${weiToEth(value)}`);
    if (reason) parts.push(`(${reason})`);
    return parts.length ? parts.join(' · ') : null;
  }
  if (kind === 'session.created') {
    return pickStr(d, 'name') ? `name: ${pickStr(d, 'name')}` : null;
  }
  return null;
}

function pickStr(d: Record<string, unknown>, k: string): string {
  const v = d[k];
  return typeof v === 'string' ? v : '';
}

function shortAddr(addr: string): string {
  return addr.length > 14 ? `${addr.slice(0, 8)}…${addr.slice(-4)}` : addr;
}

function weiToEth(wei: string): string {
  try {
    const n = BigInt(wei);
    const whole = n / 10n ** 18n;
    const frac = (n % 10n ** 18n).toString().padStart(18, '0').replace(/0+$/, '');
    return frac ? `${whole}.${frac}` : whole.toString();
  } catch {
    return wei;
  }
}
